import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import Snackbar from '@mui/material/Snackbar'
import type { SnackbarOrigin } from '@mui/material/Snackbar'
import { useTheme } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import useStore from 'reusable/mobx-store-utils'
import GapSnackbarStore from './GapSnackbarStore'

export type GapSnackbarVariant = 'success' | 'warning' | 'error' | 'info'

export interface GapSnackbarAction {
   /** text label → renders a MUI `<Button>`; omit for an icon-only action */
   label?: string
   /** icon node → renders a MUI `<IconButton>` when there's no label, otherwise the button's `startIcon` */
   icon?: ReactNode
   onClick: () => void
   /** button emphasis when a label is present. Defaults to `outlined`. */
   emphasis?: 'outlined' | 'contained'
   /** accessible label, required for icon-only actions */
   ariaLabel?: string
}

export interface GapSnackbarProps {
   open: boolean
   message: ReactNode
   onClose: () => void
   /** colored status variant. Omit for the neutral white surface (e.g. FeedbackSnackbar). */
   variant?: GapSnackbarVariant
   /** override the leading icon, or pass `false` to hide it entirely */
   icon?: ReactNode | false
   /** extra actions rendered before the close button, tinted to the variant */
   actions?: GapSnackbarAction[]
   /** `null` keeps the snackbar open until the user acts */
   autoHideDuration?: number | null
   anchorOrigin?: SnackbarOrigin
   /** set `false` to hide the auto-appended close button */
   showClose?: boolean
   /** Styles the floating root, e.g. to drop a persistent prompt below the dialog layer. */
   sx?: SxProps<Theme>
}

export default observer(function GapSnackbar({
   open,
   message,
   onClose,
   variant,
   icon,
   actions = [],
   autoHideDuration = null,
   anchorOrigin = { horizontal: 'center', vertical: 'bottom' },
   showClose = true,
   sx,
}: GapSnackbarProps) {
   const theme = useTheme()
   const gapSnackbarStore = useStore(sourceProps => new GapSnackbarStore(sourceProps), {
      message,
      variant,
      icon,
      actions,
      showClose,
      onClose,
      dark: theme.palette.mode === 'dark',
      paperColor: theme.palette.background.paper,
      shadow: theme.shadows[8],
   })

   return <Snackbar
      data-variant={variant ?? 'neutral'}
      open={open}
      onClose={gapSnackbarStore.handleClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={anchorOrigin}
      sx={sx}
      message={gapSnackbarStore.messageContent}
      action={gapSnackbarStore.allActions}
      slotProps={{ content: { sx: gapSnackbarStore.contentSx } }}
   />
})
